import React, {useCallback, useMemo, useState} from 'react'
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar} from '@mui/material'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import {useLazyQuery, useMutation} from '@apollo/client'
import Alert from '@mui/material/Alert'
import {DATASET_LIST_QUERY, SET_DATASET_PRIVATE, SET_DATASET_PUBLIC} from '@/graphql/query/queries'
import {SelectableDatasetInfo} from '@/components/pages/DataInfoPage/types'
import styled from 'styled-components'

const StrikeThroughTypography = styled(Typography)`
    text-decoration: line-through;
`

type PrivatePublicDatasetDialog = {
    datasets?: SelectableDatasetInfo[];
    type: 'public' | 'private';
    open: boolean;
    onClose: () => void;
}

const PrivatePublicDatasetDialog = ({datasets, type, open, onClose}: PrivatePublicDatasetDialog) => {
    const [datasetsErrors, setDatasetsErrors] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const [_getDatasetList, {
        refetch: refetchDatasets
    }] = useLazyQuery(DATASET_LIST_QUERY, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only'})
    const [setDatasetPublic] = useMutation(SET_DATASET_PUBLIC)
    const [setDatasetPrivate] = useMutation(SET_DATASET_PRIVATE)

    const {selectedPrivateDatasets, selectedPublicDatasets} = useMemo(() => {
        const selectedPrivateDatasets: SelectableDatasetInfo[] = []
        const selectedPublicDatasets: SelectableDatasetInfo[] = []
        datasets?.filter(ds => ds.selected).forEach(ds => {
            if(ds.public) {
                selectedPublicDatasets.push(ds)
            } else {
                selectedPrivateDatasets.push(ds)
            }
        })

        return {selectedPrivateDatasets, selectedPublicDatasets}
    }, [datasets])

    const areSomeSelected = useMemo((): boolean => {
        return selectedPrivateDatasets.length > 0 || selectedPublicDatasets.length > 0
    }, [selectedPrivateDatasets, selectedPublicDatasets])

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose()
        }
    }, [])

    const handleConfirm = useCallback(async () => {
        if (areSomeSelected && !!type) {
            setLoading(true)

            let promises
            if (type === 'public') {
                promises = selectedPrivateDatasets
                    .map(ds => setDatasetPublic({variables: {datasetId: ds.dataset_id}})
                        .then(data => ({id: ds.dataset_id, data, error: null}))
                        .catch(error => ({id: ds.dataset_id, data: null, error}))
                    )
            } else {
                promises = selectedPublicDatasets
                    .map(ds => setDatasetPrivate({variables: {datasetId: ds.dataset_id}})
                        .then(data => ({id: ds.dataset_id, data, error: null}))
                        .catch(error => ({id: ds.dataset_id, data: null, error}))
                    )
            }

            const results = await Promise.all(promises)

            results.forEach(result => {
                if (result.error || (result.data?.errors && result.data.errors.length > 0)) {
                    setDatasetsErrors(prev => [...prev, result.id])
                }
            })

            await refetchDatasets()
            setLoading(false)
            handleClose()
        }
    }, [datasets, type, areSomeSelected, handleClose])

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="private-public-dialog-title"
                aria-describedby="private-public-dialog-description"
            >
                <DialogTitle id="private-public-dialog-title" variant="h5">
                    {areSomeSelected ? `Are you sure you want to make datasets ${type}?` : 'No datasets selected'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="private-public-dialog-description">
                        {areSomeSelected ? (
                            <>
                                {(type === 'private' ? selectedPublicDatasets : selectedPrivateDatasets).map(ds => (
                                    <Typography key={ds.dataset_id} color={ThemeColorsHospitalic.TEXT_PRIMARY}>
                                        Dataset id: {ds?.dataset_id},
                                        Dataset name: {ds?.dataset_name}
                                    </Typography>
                                ))}
                                {(type === 'private' ? selectedPrivateDatasets : selectedPublicDatasets).map(ds => (
                                    <StrikeThroughTypography key={ds.dataset_id} color={ThemeColorsHospitalic.TEXT_DISABLED}>
                                        Dataset id: {ds?.dataset_id},
                                        Dataset name: {ds?.dataset_name}
                                    </StrikeThroughTypography>
                                ))}
                            </>
                        ) : (
                            <Typography color={ThemeColorsHospitalic.TEXT_PRIMARY}>
                                Please select datasets to make them {type} in the dataset table.
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined" size="small">Back</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!areSomeSelected || (type === 'private' && selectedPublicDatasets.length < 1) || (type === 'public' && selectedPrivateDatasets.length < 1)}
                        loading={loading}
                        autoFocus
                        variant="contained" size="small">
                        Make {type}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={datasetsErrors.length > 0}
                autoHideDuration={3000}
                onClose={() => setDatasetsErrors([])}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}>
                <Alert
                    onClose={() => setDatasetsErrors([])}
                    severity="error"
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    Error making datasets with id {type}: {datasetsErrors.join(', ')}
                </Alert>
            </Snackbar>
        </>
    )
}

export default PrivatePublicDatasetDialog